'use client'

import Link from 'next/link'
import s from './StoriesComponent.module.scss'
import { useEffect, useRef } from 'react'
import FeaturedProduct from '../../Singles/FeaturedProduct'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import FeaturedStory from '@/components/Singles/FeaturedStory'
import SelectedWorkBlock from '@/components/Common/SelectedWorkBlock'

type SelectedBlock = { title?: string; contentLeft?: any[]; contentRight?: any[] } | null
type Props = { data: any[]; selectedBlock?: SelectedBlock }

gsap.registerPlugin(ScrollTrigger)

export default function StoriesComponent({ data, selectedBlock }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const container = containerRef.current
    const list = listRef.current
    if (!container || !list) return

      // flag global para suprimir refresh durante hover/cycle
      ; (window as any).__suppressST = false

    let prevWidth = list.scrollWidth
    let t: ReturnType<typeof setTimeout> | null = null

    const safeRefresh = () => {
      if ((window as any).__suppressST) return // 🔒 no refrescar durante hover
      const w = list.scrollWidth
      if (w !== prevWidth) {
        prevWidth = w
        // debounce suave
        if (t) clearTimeout(t)
        t = setTimeout(() => ScrollTrigger.refresh(), 80)
      }
    }

    // SOLO ResizeObserver y SOLO si cambia el ancho
    const ro = new ResizeObserver(() => safeRefresh())
    ro.observe(list)

    // opcional: resize de ventana también pasa por safeRefresh
    const onWinResize = () => safeRefresh()
    window.addEventListener('resize', onWinResize)

    return () => {
      ro.disconnect()
      if (t) clearTimeout(t)
      window.removeEventListener('resize', onWinResize)
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current!;
    const list = listRef.current!;
    if (!container || !list) return;

    const mm = gsap.matchMedia();

    // Handlers guardados (no anónimos inline)
    const handleResize = () => ScrollTrigger.refresh();

    mm.add("(min-width: 1025px) and (prefers-reduced-motion: no-preference)", () => {
      // tween + ST creados juntos; matar el tween mata su ST de forma segura
      const tween = gsap.to(list, {
        x: () => -(list.scrollWidth - window.innerWidth),
        ease: "none",
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: () => "+=" + (list.scrollWidth - window.innerWidth),
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      // ResizeObserver debounced
      let t: ReturnType<typeof setTimeout> | null = null;
      const ro = new ResizeObserver(() => {
        if (t) clearTimeout(t);
        t = setTimeout(() => ScrollTrigger.refresh(), 80);
      });
      ro.observe(list);

      window.addEventListener("resize", handleResize);

      // cleanup SOLO de este scope
      return () => {
        window.removeEventListener("resize", handleResize);
        ro.disconnect();
        if (t) clearTimeout(t);
        tween.kill();           // ← mata su ScrollTrigger y pin-spacer correctamente
      };
    });

    mm.add("(max-width: 1024px)", () => {
      // sin pin; limpia cualquier transform pendiente
      gsap.killTweensOf(list);
      gsap.set(list, { clearProps: "transform" });

      return () => {
        gsap.set(list, { clearProps: "transform" });
      };
    });

    // cleanup global: revierte TODO lo creado por mm.add
    return () => mm.revert();
  }, []);

  useEffect(() => {
    const setCooldown = (ms: number) => {
      ; (window as any).__scrollCooldownUntil = performance.now() + ms
    }
    const onScrollStart = () => setCooldown(250) // mientras scrub
    const onScrollEnd = () => setCooldown(200)   // un pequeño colchón tras parar

    ScrollTrigger.addEventListener('scrollStart', onScrollStart)
    ScrollTrigger.addEventListener('scrollEnd', onScrollEnd)

    return () => {
      ScrollTrigger.removeEventListener('scrollStart', onScrollStart)
      ScrollTrigger.removeEventListener('scrollEnd', onScrollEnd)
    }
  }, [])

  return (
    <>
    <div ref={containerRef} className={s.stories}>
      {data?.length > 0 && (
        <ul ref={listRef} className={s.listStories}>
          {data.map((rel: any, i: number) => (
            <li key={i} className={s.item}>
              <FeaturedStory data={rel} vertical={rel.featuredImage.verticalHorizontal === 'vertical'} priority={i < 2} />
            </li>
          ))}
        </ul>
      )}
    </div>
    <SelectedWorkBlock data={selectedBlock} />
    </>
  )
}